import React, { useEffect, useState } from "react";
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { OtterAssistSetupFields, emptyAssist, serializeAssist, setupValid } from "@/components/social/OtterAssistComposerPanel";

// Otter Assist V1 Phase 5 — **Edit Otter Assist** (OAD-061). Changes matching configuration
// WITHOUT touching the public Chatter text. A save that changes nothing is a true no-op; a
// material change creates a new immutable version and reruns matching, and never charges a
// second Assist. Reuses the shared setup form and the existing versioning/rerun pipeline.

/** Rehydrate the shared setup shape from the stored version the backend returns. */
const fromConfig = (config) => {
  const filters = config?.filters || [];
  const pick = (kind) => filters.find((f) => f.kind === kind);
  const industry = filters.filter((f) => f.kind === "industry");
  const geo = pick("geo");
  const work = pick("work_setting");
  return {
    ...emptyAssist(),
    interpreted_need: config?.interpreted_need || "",
    audience_scope: config?.audience_scope || "community",
    include_followers: !!config?.include_followers,
    include_fans: !!config?.include_fans,
    industry_values: industry.map((f) => f.value),
    industry_mode: industry[0]?.mode || "none",
    geo: geo
      ? { geoOn: true, radius: geo.value?.radius_mi || 25,
          center: { lat: geo.value?.lat, lng: geo.value?.lng, label: geo.value?.label,
                    granularity: geo.value?.granularity, state_code: geo.value?.state_code,
                    country_code: geo.value?.country_code } }
      : { geoOn: false, center: null, radius: 25 },
    geo_mode: geo?.mode || "none",
    work_context: work?.value || config?.work_context || null,
    work_mode: work?.mode || "none",
    urgency: config?.urgency || "soon",
    urgency_note: config?.urgency_note || "",
    within_days_count: config?.within_days_count || null,
    sender_timezone: config?.sender_timezone || emptyAssist().sender_timezone,
  };
};

export function OtterAssistEditDialog({ assistId, industryOpts, identityType, onClose }) {
  const [memberConfig, setMemberConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editable, setEditable] = useState(true);
  const [charged, setCharged] = useState(false);
  const [setup, setSetup] = useState(emptyAssist);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [{ data }, cfg] = await Promise.all([
          api.get(`/otter-assist/assists/${assistId}/config`),
          api.get(`/otter-assist/config`, { params: { identity_type: identityType } }),
        ]);
        if (!active) return;
        setMemberConfig(cfg.data);
        setSetup(fromConfig(data.config));
        setEditable(!!data.editable);
        setCharged(!!data.charged);
      } catch (e) {
        toast.error("We couldn't open your Otter Assist settings.");
        onClose(false);
      } finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [assistId, identityType, onClose]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/otter-assist/assists/${assistId}/config`,
                                      serializeAssist(setup));
      if (data.material) {
        toast.success(charged
          ? "Your Otter Assist settings changed. We'll refresh your matches. This will not use another Assist."
          : "Your Otter Assist settings changed. We'll refresh your matches. You won't be charged unless the Assist is activated under the normal match rules.");
      } else {
        toast.success("Nothing changed, so your matches stay as they are.");
      }
      onClose(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "We couldn't save those changes.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(false); }}>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-lg"
        data-testid="otter-assist-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Sparkles className="h-4 w-4 text-teal-700" /> Edit Otter Assist
          </DialogTitle>
          <DialogDescription>
            This changes who Otter Assist looks for. Your post stays exactly as you wrote it.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading your settings…
          </div>
        ) : !editable ? (
          <p className="text-sm text-foreground" data-testid="otter-assist-edit-terminal">
            This Otter Assist is finished, so its settings can't be changed. Start a new post when
            you need help again.
          </p>
        ) : (
          <>
            <OtterAssistSetupFields config={memberConfig} setup={setup} setSetup={setSetup}
              identityType={identityType} industryOpts={industryOpts} idSuffix="-edit" />
            <p className="text-[11px] text-muted-foreground">
              {charged
                ? "Refreshing your matches never uses another Assist."
                : "You won't be charged unless this Assist activates under the normal match rules."}
            </p>
          </>
        )}

        <DialogFooter>
          {editable && !loading && (
            <Button type="button" disabled={busy || !setupValid(setup, memberConfig)}
              onClick={save} data-testid="otter-assist-edit-save">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save changes
            </Button>
          )}
          <Button type="button" variant="ghost" onClick={() => onClose(false)}
            data-testid="otter-assist-edit-cancel">Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default OtterAssistEditDialog;
